import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgFor, NgIf, NgOptimizedImage, TitleCasePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { Users } from 'app/modal/users';
import { Scheme, FuseConfigService } from '@fuse/services/config';
import { Subject, takeUntil } from 'rxjs';
import { Roles } from "../../../modal/roles";
import { UsersService } from "app/services/users.service";
import { Constants } from "../../../constants/constants";
import { SnackBarService } from "app/services/snack-bar.service";
import { FuseConfirmationService } from "../../../../@fuse/services/confirmation";
import { UserRoleService } from 'app/services/use-role.service';
import { AppSettingService } from "../../../services/app-setting.service";

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [MatButtonModule, MatMenuModule, NgIf, MatIconModule, NgClass, NgFor, MatDividerModule, RouterLink, NgOptimizedImage, TitleCasePipe]
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: Users;
    rolesList: Roles[];
    config: any;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _usersService: UsersService,
        private _snackBarService: SnackBarService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _userRoleService: UserRoleService,
        protected appSettingService: AppSettingService,
        private _fuseConfigService: FuseConfigService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: Users) => {
                this.user = user;
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });


        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: any) => {
                this.config = config;
                this._changeDetectorRef.markForCheck();
            });

        this._userRoleService.loggedInUserRoles$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((roles: Roles[]) => {
                this.rolesList = roles;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.user,
            status,
        }).subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }


    /**
     * Set the layout
     */
    setLayout(layout: string): void {
        this._router.navigate([], {
            queryParams: { layout: null },
            queryParamsHandling: 'merge',
        }).then(() => {
            this._fuseConfigService.config = { layout };
        });
    }

    /**
     * Set the scheme
     */
    setScheme(scheme: Scheme): void {
        this._router.navigate([], {
            queryParams: { scheme: null },
            queryParamsHandling: 'merge',
        }).then(() => {
            this._fuseConfigService.config = { scheme };
        });
    }

    updateUserRole(role: Roles) {

        const confirmation = this._fuseConfirmationService.open({
            title: 'Change role',
            message: 'Are you sure you want to change active role? Your workspace will be reloaded.',
            actions: {
                confirm: {
                    label: 'Confirm',
                },
            },
        });

        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if (result === 'confirmed') {
                this._usersService.updateActiveRole(this.user.id, role.roleid)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((response: any) => {
                        if (response.status == Constants.SUCCESS) {
                            this._snackBarService.openSnackBar(Constants.RELOAD_ON_ROLE_CHANGE);
                            this.redirect()
                        }
                        this._changeDetectorRef.markForCheck();
                    },
                        (response) => {
                            this._snackBarService.openSnackBar(response?.error?.message);
                        }
                    );

                // Mark for check
                this._changeDetectorRef.markForCheck();
            }
        });
    }


    private redirect() {
        this._usersService.getHomePageURL()
            .subscribe((response: any) => {
                //this._router.navigateByUrl(response.redirectURL);
                this._router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
                    this._router.navigate([response.redirectURL])
                );
            },
                (response) => {
                    this._snackBarService.openSnackBar(response?.error?.message);
                }
            );
    }
}
